import React, { Fragment, useContext, useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { QueryClient, useQuery } from 'react-query';
import GoogleMapReact from 'google-map-react';
import withStore from 'context/Store/withStore';
import { LanguageContext } from 'context/Language';
import { StoreContext } from 'context/Store';
import { MapProps, Marker } from 'components/Map/Marker';
import { articles, postalOffices } from 'api';
import { useTranslation } from 'hooks';
import { getQueries, getSeoTitle, defaultOptions, getSeoDescription, cacheTime } from 'utils';
import { ArticleTags, Properties } from 'types';
import { getWorkHours } from 'libs';
import { officeTypeToCode } from 'utils/office-type-to-code';

const Container = dynamic(() => import('components/Grid/Container'));
const Row = dynamic(() => import('components/Grid/Row'));
const Col = dynamic(() => import('components/Grid/Col'));
const Loader = dynamic(() => import('components/Loader'));
const Section = dynamic(() => import('components/Layout/Section'));
const Button = dynamic(() => import('components/Button'));
const Input = dynamic(() => import('components/Input'));

const key = process.env.NEXT_PUBLIC_MAP_KEY;

const PostalOfficeStreet: React.FC<{
  city: Properties;
  btnText: React.ReactNode;
  search: string;
  setCityEmptySearch: React.Dispatch<React.SetStateAction<number[]>>;
}> = ({ city: cityList, btnText, search, setCityEmptySearch }) => {
  const streets = cityList.streets.filter(({ name }) =>
    name
      ?.trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .includes(search),
  );
  const [loadMore, setLoadMore] = useState(10);
  const firstRender = useRef(false);
  const showBtn = streets?.slice(0, loadMore).length === 10 && streets?.length > 10;

  useEffect(() => {
    firstRender.current && setLoadMore(streets?.length);
    firstRender.current = true;
    !streets?.length
      ? setCityEmptySearch((value) => [...value.filter((id) => id !== cityList.id), cityList.id])
      : setCityEmptySearch((value) => value.filter((id) => id !== cityList.id));
  }, [search]);

  return (
    <>
      <ul className="postal-office__container--wrapper">
        {streets &&
          streets.length > 0 &&
          streets.slice(0, loadMore).map((street) => {
            if (street.name) {
              return (
                <li key={street?.id} className="postal-office__container--data">
                  <div className="flex" style={{ gap: '1rem', justifyContent: 'space-between' }}>
                    {street?.name && <span>{street.name}</span>}
                    {street?.blocks && <span style={{ textAlign: 'end' }}>{street.blocks}</span>}
                  </div>
                </li>
              );
            }
          })}
      </ul>
      {showBtn && (
        <Button
          style={{ width: 'fit-content', margin: 'auto' }}
          type="dark"
          onClick={() => setLoadMore(streets?.length)}
        >
          {btnText}
        </Button>
      )}
    </>
  );
};

const PostalOfficePage: NextPage<Properties> = ({ data }) => {
  const { t } = useTranslation(['common']);
  const { query } = useRouter();
  const { lang } = useContext(LanguageContext);
  const { url } = useContext(StoreContext);
  const [office_type, zip_code] = query?.slug.toString().split('-');
  const [{ lat, lng }, setState] = useState<MapProps<number>>({
    lat: undefined,
    lng: undefined,
  });

  const [search, setSearch] = useState('');
  const [cityEmptySearch, setCityEmptySearch] = useState<number[]>([]);

  const { data: postalOffice, isLoading } = useQuery(
    ['postal-office', { office_type: office_type, zip_code: zip_code }],
    postalOffices.getList,
    {
      cacheTime,
      onSuccess: (data) => {
        if (data.results && data.results.length > 0) {
          setState({
            lat: +data?.results[0]?.address?.geolocation?.coordinates[1],
            lng: +data?.results[0]?.address?.geolocation?.coordinates[0],
          });
        }
      },
    },
  );
  const onChange = (items) => {
    return items?.reduce(
      (results, currentValue) => (
        (results[currentValue?.city?.region?.id] = {
          ...results[currentValue?.city?.region?.id],
          name: currentValue?.city?.region?.name,
          id: currentValue?.city?.region?.id,
          city: {
            ...results[currentValue?.city?.region?.id]?.city,
            [currentValue?.city?.id]: {
              ...results[currentValue?.city?.region?.id]?.city[currentValue?.city?.id],
              name: currentValue?.city?.name,
              id: currentValue?.city?.id,
              streets: [
                ...(results[currentValue?.city?.region?.id]?.city[currentValue?.city?.id]
                  ?.streets || []),
                {
                  id: currentValue?.id,
                  name: currentValue?.name,
                  postal_office: currentValue?.postal_office,
                  blocks: currentValue?.blocks,
                },
              ],
            },
          },
        }),
        results
      ),
      {},
    );
  };

  return (
    <>
      <Head>
        <title>{getSeoTitle(data?.results[0]?.i18n && data?.results[0]?.i18n[lang]?.title)}</title>
        {data?.results[0]?.i18n && data?.results[0]?.i18n[lang]?.preview && (
          <meta
            name="description"
            content={getSeoDescription(data?.results[0]?.i18n[lang].preview)}
          />
        )}

        <meta property="og:url" content={url} />
        <meta
          name="og:title"
          content={getSeoTitle(data?.results[0]?.i18n && data?.results[0]?.i18n[lang]?.title)}
        />
        {data?.results[0]?.i18n &&
          (data?.results[0]?.i18n[lang]?.preview || data?.results[0]?.i18n[lang]?.content) && (
            <meta
              property="og:description"
              content={getSeoDescription(
                data?.results[0]?.i18n[lang]?.preview || data?.results[0]?.i18n[lang]?.content,
              )}
            />
          )}
        {data?.results[0]?.background && (
          <meta property="og:image" content={data?.results[0]?.background} />
        )}
      </Head>

      <Container className="postal-office mt-16">
        <Loader loading={isLoading}>
          {postalOffice?.results?.map((office) => (
            <Row g={4}>
              {office?.address?.geolocation && (
                <Col size={12} className="postal-office__map">
                  {!lat && !lng ? (
                    <div className="map-loader" />
                  ) : (
                    <GoogleMapReact
                      bootstrapURLKeys={{ key }}
                      center={{ lat, lng }}
                      defaultZoom={12}
                    >
                      {office?.address?.geolocation?.coordinates &&
                      office?.address.geolocation.coordinates.length > 1 ? (
                        <Marker
                          key={office?.id}
                          zip_code={office.zip_code}
                          region={office?.address?.region?.name}
                          city={office?.address?.city?.name}
                          street={office?.address?.street?.name}
                          block={office?.address?.block?.name}
                          office_type={office?.office_type}
                          lat={office?.address?.geolocation?.coordinates[1]}
                          lng={office?.address?.geolocation?.coordinates[0]}
                          isOpen={office?.id}
                        />
                      ) : null}
                    </GoogleMapReact>
                  )}
                </Col>
              )}
              <Col size={12}>
                <Container className="postal-office__container">
                  <Row g={4}>
                    <Col size={12}>
                      <Section>
                        <h1 className="section__subtitle">{office.name}</h1>
                      </Section>
                    </Col>
                    <Col size={12} md={6} className="postal-office__box">
                      <h2 className="postal-office__container--title">
                        {`${t('contact.address')}: `}
                      </h2>
                      <p className="postal-office__container--data">{`${
                        office.prefix || officeTypeToCode[office.office_type]
                      }-${office.zip_code} ${office?.address?.city?.name || ''}`}</p>
                      {office?.address?.country?.name && (
                        <p className="postal-office__container--data">{`${office?.address?.country?.name}`}</p>
                      )}
                      <p className="postal-office__container--data">
                        {[
                          office?.address?.street,
                          office?.address?.block,
                          office?.address?.city?.name,
                          office?.address?.region?.name,
                        ]
                          .filter((el) => el)
                          .join(', ')}
                      </p>
                    </Col>

                    {!!office?.work_days?.length && (
                      <Col size={12} md={6} className="postal-office__box">
                        <h2 className="postal-office__container--title">{`${t(
                          'contact.workProgram',
                        )}: `}</h2>
                        {office?.work_days?.map?.((workDay, i, array) => (
                          <p
                            style={{ marginBottom: i !== array.length - 1 ? '10px' : undefined }}
                            className="postal-office__container--data"
                            key={i}
                          >
                            <span>
                              {Object.values(workDay.days)
                                ?.map((el) => t(`${el}`))
                                .join(', ')}
                              :
                            </span>
                            <span>
                              {getWorkHours(workDay.work_hours_from, workDay.work_hours_to)}
                            </span>
                            <br />
                            <span>{`${t('lunchBreak')}: `}</span>
                            {workDay.break_hours_from && workDay.break_hours_to ? (
                              <span>
                                {getWorkHours(workDay.break_hours_from, workDay.break_hours_to)}
                              </span>
                            ) : (
                              t('withoutInterruptions')
                            )}
                          </p>
                        ))}
                      </Col>
                    )}

                    {(office?.email || office?.phone_numbers?.length > 0) && (
                      <Col size={12} md={6} className="postal-office__box">
                        <h2 className="postal-office__container--title">{`${t(
                          'common.contacts',
                        )}:`}</h2>
                        <p className="postal-office__container--data">
                          {office.email && (
                            <div>
                              {t('common.email')}: {office?.email}
                            </div>
                          )}
                          {office.phone_numbers?.length > 0 && (
                            <div>
                              {t('common.phone')}: {office?.phone_numbers?.join(', ')}
                            </div>
                          )}
                        </p>
                      </Col>
                    )}

                    {office?.served_cities?.length > 0 && (
                      <Col size={12} md={6} className="postal-office__box">
                        <h2 className="postal-office__container--title">{`${t(
                          'common.servedCities',
                        )}:`}</h2>
                        <p className="postal-office__container--data text-align-justify">
                          {office?.served_cities
                            ?.map((city) => city?.name)
                            ?.sort()
                            ?.join(', ')}
                        </p>
                      </Col>
                    )}

                    {office.additional_info && (
                      <Col size={12} className="postal-office__box">
                        <div className="postal-office__container--data">
                          {office?.additional_info.split('\n').map((item, i) => (
                            <p key={i} dangerouslySetInnerHTML={{ __html: item }} />
                          ))}
                        </div>
                      </Col>
                    )}

                    {office?.streets && office?.streets.length > 0 && (
                      <Col size={12} className="postal-office__card">
                        <Section>
                          <h1 className="section__subtitle">{t('listStreetsServed')}</h1>
                        </Section>

                        <div className="mt-16">
                          <p
                            className="color-active-background"
                            style={{ fontWeight: 600, marginBottom: '0.3rem' }}
                          >
                            {t('search')}
                          </p>
                          <Input
                            type="text"
                            placeholder={t('street')}
                            value={search}
                            onChange={(value) =>
                              setSearch(
                                String(value)
                                  .trim()
                                  .toLowerCase()
                                  .normalize('NFD')
                                  .replace(/[\u0300-\u036f]/g, ''),
                              )
                            }
                          />
                        </div>

                        {Object.values(onChange(office?.streets)).map((region: Properties) => (
                          <Fragment key={region?.id}>
                            {region?.name && (
                              <h2 className="postal-office__container--title mt-16">
                                {region?.name}
                              </h2>
                            )}
                            {Object.values(region.city).map((city: Properties) => (
                              <Fragment key={city?.id}>
                                {city?.name && !cityEmptySearch.includes(city?.id) && (
                                  <h3 className="postal-office__container--subtitle mt-16">
                                    {city?.name}
                                  </h3>
                                )}

                                <PostalOfficeStreet
                                  city={city}
                                  btnText={t('loadMore')}
                                  search={search}
                                  setCityEmptySearch={setCityEmptySearch}
                                />
                              </Fragment>
                            ))}
                            {Object.keys(region.city).length === cityEmptySearch.length && (
                              <div>{t('NoResults')}</div>
                            )}
                          </Fragment>
                        ))}
                      </Col>
                    )}
                  </Row>
                </Container>
              </Col>
            </Row>
          ))}
        </Loader>
      </Container>
    </>
  );
};

PostalOfficePage.getInitialProps = async ({ query }): Promise<Properties> => {
  const queryClient = new QueryClient(defaultOptions);

  await queryClient.prefetchQuery(
    ['data', { tags: ArticleTags.MAP, page_size: 1 }],
    (props) => articles.getList(props, { 'Accept-Language': query?.lang }),
    { cacheTime },
  );

  return getQueries(queryClient);
};

export default withStore()(PostalOfficePage);
